import { getPageSize } from '../utils/pageSize';
import { loadData } from '../utils/ApiUtil';
import { find, isString } from 'lodash';
import { JsonApiRecord, JsonApiRelationship } from './types';

interface UrlParams {
  authToken?: string;
  includes?: string;
  filters?: Record<string, (string | boolean | number | undefined) | Array<string | boolean | number | undefined>>;
  sorts?: Array<{
    value: string;
    field?: string;
    direction?: 'asc' | 'desc';
  }>;
  page?: { number?: number; size?: number };
  query?: Record<string, { term: string; fields: Record<string, string> }>;
  fields?: Record<string, string>;
}
export const buildUrl = (base: string, params: UrlParams = {}) => {
  const { includes, filters, sorts, page, query, fields } = params;
  const filterParams = getFilterParams(filters);
  const sortParams = getSortParams(sorts);
  const pageParams = getPageParams(page);
  const queryParams = getQueryParams(query);
  const fieldParams = getFieldParams(fields);

  const paramString = [
    `${filterParams}`,
    `${includes ? `include=${includes}` : ''}`,
    `${sortParams ? `sort=${sortParams}` : ''}`,
    `${pageParams ? `${pageParams}` : ''}`,
    `${queryParams ? `${queryParams}` : ''}`,
    `${fieldParams ? `${fieldParams}` : ''}`,
  ]
    .filter(Boolean)
    .join('&');

  return `${base}${base.indexOf('?') === -1 ? '?' : '&'}${paramString}`;
};

export const getFilterParams = (filters: UrlParams['filters']) => {
  if (!filters) return '';

  if (isString(filters)) return filters;

  return Object.keys(filters)
    .filter(key => filters[key] !== null && filters[key] !== undefined)
    .map(key => {
      const filter = filters[key];
      if (filter instanceof Array) {
        return filter.map(f => `filter[${key}][]=${encodeURIComponent(f || '')}`).join('&');
      } else {
        return `filter[${key}]=${encodeURIComponent(filter ?? '')}`;
      }
    })
    .join('&');
};

export const getSortParams = (
  sorts:
    | Array<{
        value: string;
        field?: string;
        direction?: 'asc' | 'desc';
      }>
    | undefined
) => {
  if (!sorts) {
    return '';
  }
  if (!Array.isArray(sorts)) {
    sorts = [sorts];
  }
  return sorts
    .map(sort => {
      if (!sort) return '';
      return `${sort.direction === 'desc' ? '-' : ''}${sort.value}`;
    })
    .filter(Boolean)
    .join(',');
};

export const getPageParams = (page: { number?: number; size?: number } | number | undefined) => {
  if (typeof page === 'object' && page !== undefined) {
    page.size = page.size || getPageSize();
    page.number = page.number || 1;
  } else if (Number.isInteger(page) && page !== undefined) {
    page = { number: page, size: getPageSize() };
  } else {
    page = { number: 1, size: getPageSize() };
  }

  return page ? `page[number]=${page.number || 1}&page[size]=${page.size}` : '';
};

export const getQueryParams = (query: Record<string, { term: string; fields: Record<string, string> }> | undefined) => {
  if (!query) return '';

  return Object.entries(query)
    .filter(([_, value]) => value !== null && value !== undefined)
    .filter(([_, value]) => !!value.term)
    .map(([key, value]) => {
      let param = `query[${key}][term]=${encodeURIComponent(value.term)}`;
      const fields = Object.entries(value.fields)
        .map(([fieldKey, fieldValue]) => {
          return `${fieldKey}.${fieldValue}`;
        })
        .join(',');
      param += `&query[${key}][fields]=${fields}`;
      return param;
    })
    .join('&');
};

export const getFieldParams = (fields: Record<string, string> | undefined) => {
  if (!fields) return '';

  return Object.entries(fields)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `fields[${key}]=${value}`)
    .join('&');
};

export const getData = async (base: string, params: UrlParams = {}) => {
  const url = buildUrl(base, params);
  const { authToken } = params;
  let headers = {};
  if (authToken) {
    headers = {
      Authorization: `Bearer ${authToken}`,
    };
  }
  const json = await loadData(
    url,
    {
      headers,
      mode: 'cors',
    },
    false
  );
  const data = buildFromJsonApi(json);
  return {
    data,
    meta: json.meta,
  };
};

function buildFromJsonApi<T>(json: { data: JsonApiRecord<T> | JsonApiRecord<T>[]; included: JsonApiRecord<any>[] }) {
  return buildDataFromJsonApi(json.data, json.included);
}

function buildDataFromJsonApi<T>(data: JsonApiRecord<T> | JsonApiRecord<T>[], included: JsonApiRecord<any>[]): any {
  if (Array.isArray(data)) {
    return data.map(dataValue => buildDataObjectFromJsonApi(dataValue, included));
  }
  return buildDataObjectFromJsonApi(data, included);
}

function buildDataObjectFromJsonApi<T>(data: JsonApiRecord<T>, included: JsonApiRecord<any>[]) {
  return Object.assign(
    { id: data.id },
    data.attributes,
    data.relationships && included
      ? Object.entries(data.relationships).reduce(
          (object, [key, value]) => ({
            ...object,
            [key]: buildRelationshipFromJsonApi(value.data, included),
          }),
          {}
        )
      : {}
  );
}

function buildRelationshipFromJsonApi<T>(
  data: JsonApiRelationship | JsonApiRelationship[],
  included: JsonApiRecord<any>[]
): any {
  if (!data) return;
  if (Array.isArray(data)) {
    return data.map(dataValue => buildRelationshipObjectFromJsonApi(dataValue, included));
  }
  return buildRelationshipObjectFromJsonApi(data, included);
}

function buildRelationshipObjectFromJsonApi(object: JsonApiRelationship, included: JsonApiRecord<any>[]): any {
  const includedObject = find(included, inc => inc.id === object.id && inc.type === object.type);
  if (!includedObject) {
    return null;
  }
  return buildDataFromJsonApi(includedObject, included);
}
